/**
 * The fields a connection pool is DIALLED from — one projection, two readers.
 *
 * Both of these have to agree about what "the same connection" means, and they
 * had drifted:
 *
 *  - `dialFingerprint` (`electron/connection-managers/connectInFlightJoin.ts`)
 *    decides whether a registered pool was dialled from the requester's config,
 *    i.e. whether it may be reused or is stale.
 *  - `connectionRequiresPoolReset` decides whether SAVING an edit invalidates
 *    the live pool.
 *
 * The second listed a hand-picked subset, so editing an Oracle service name,
 * a TNS alias, a wallet location, an Entra tenant or an SSL/encrypt flag left
 * the connection serving queries from a pool dialled with the OLD value — a
 * pool the fingerprint would have called stale on the very next connect. One
 * list, derived twice, is the only way those two answers can be the same one.
 *
 * ## `target` vs `secrets`
 *
 * `target` is everything that can be COMPARED between a saved record and a
 * form: hosts, ports, names, flags, auth kinds. `secrets` is everything that
 * cannot — the DB password is persisted encrypted while a form carries
 * plaintext, so comparing the two would report a change on every save. The
 * fingerprint hashes both (it only ever sees one shape at a time); the
 * pool-reset predicate compares `target` and takes an explicit signal for the
 * secrets (the save's `passwordEdited` option, and the SSH form-only fields).
 */
import type { Connection } from '../../../types/electron';
import { sshPortIdentityKey } from '../../../types/sshPort';
import { storageResourceKey } from '../../../types/azureStorage';

export interface DialProjection {
  /** Comparable between a stored record and a form. */
  target: Record<string, string | number | boolean>;
  /** Never comparable across the persistence boundary — hashed, or signalled explicitly. */
  secrets: Record<string, string>;
}

/**
 * Project the dial-relevant shape of a connection.
 *
 * A new dial-relevant `Connection` field must be added here, or two dials
 * differing only in it will join, and saving a change to it will not rebuild
 * the pool.
 */
export function projectDialFields(config: Connection): DialProjection {
  const ssh = config.sshTunnel;
  const sshEnabled = ssh?.enabled === true;

  return {
    target: {
      engine: config.engine ?? 'mssql',
      server: config.server ?? '',
      port: String(config.port ?? ''),
      filePath: config.filePath ?? '',
      database: config.database ?? '',
      authentication: config.authentication ?? '',
      login: config.login ?? '',
      entraTenantId: config.entraTenantId ?? '',
      ssl: config.ssl ?? false,
      encrypt: config.encrypt ?? 'default',
      trustServerCertificate: config.trustServerCertificate ?? false,
      oracleServiceName: config.oracleServiceName ?? '',
      oracleUseSid: config.oracleUseSID ?? false,
      oracleInitialSchema: config.oracleInitialSchema ?? '',
      oracleConnectionStringType: config.oracleConnectionStringType ?? 'ezconnect',
      oracleTnsDescriptor: config.oracleTnsDescriptor ?? '',
      oracleTnsAlias: config.oracleTnsAlias ?? '',
      oracleTnsAdmin: config.oracleTnsAdmin ?? '',
      oracleWalletLocation: config.oracleWalletLocation ?? '',
      // Only SQLite dials from it (`openSqliteDb({ readOnly })`), but it IS a
      // dial field: a handle opened writable is not the handle a read-only
      // connection asked for, and saving the flip must rebuild it. One list
      // for every engine — the other engines' pools simply never differ in it
      // for the same saved connection (R9).
      readOnly: config.readOnly ?? false,
      // Azure Storage dials from its endpoints, enabled services, attachment
      // scope and emulator flag (spec §4.4). `azureResourceId` is NOT a dial
      // field on its own: it names the ARM resource for the portal link and
      // changes nothing about how the clients dial — except under account-key
      // data access (below).
      azureStorageEndpoints: JSON.stringify(
        Object.entries(config.azureStorageEndpoints ?? {})
          .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
          .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      ),
      azureStorageServices: [...(config.azureStorageServices ?? [])].sort().join(','),
      azureStorageScope:
        config.azureStorageScope?.kind === 'resource' ? storageResourceKey(config.azureStorageScope.resource) : 'account',
      azureStorageEmulator: config.azureStorageEmulator ?? false,
      // How DATA is reached (RBAC fix 2026-09-25): the account key read via ARM dials Shared Key clients, and under it
      // the resource ID names the account whose key is read — so both are dial fields there, and only there.
      azureStorageDataAccess: config.azureStorageDataAccess ?? 'entra',
      azureStorageKeyAccount: config.azureStorageDataAccess === 'account-key-via-arm' ? config.azureResourceId ?? '' : '',
      // The tunnel is part of the dial: the same server reached directly and
      // through a bastion are two different pools.
      sshEnabled,
      sshHost: sshEnabled ? ssh?.host ?? '' : '',
      // Through the one normaliser (P1): `2222` and `'2222'` are one dial, and
      // a malformed port never keys as the default.
      sshPort: sshEnabled ? sshPortIdentityKey(ssh?.port) : '',
      sshUser: sshEnabled ? ssh?.user ?? '' : '',
      sshAuthKind: sshEnabled ? ssh?.auth?.kind ?? '' : '',
      sshKeyPath: sshEnabled && ssh?.auth?.kind === 'key' ? ssh.auth.keyPath ?? '' : '',
      sshConfigAlias: sshEnabled ? ssh?.sshConfigAlias ?? '' : '',
    },
    secrets: {
      password: config.password ?? '',
      sshPassword: sshEnabled ? (ssh as { password?: string })?.password ?? '' : '',
      sshPassphrase: sshEnabled ? (ssh as { passphrase?: string })?.passphrase ?? '' : '',
    },
  };
}

/**
 * Which projected `target` fields differ. Empty means the two configs would
 * dial the same pool, as far as anything comparable can tell.
 */
export function changedDialFields(a: Connection, b: Connection): string[] {
  const left = projectDialFields(a).target;
  const right = projectDialFields(b).target;
  return Object.keys(left).filter((key) => left[key] !== right[key]);
}
